import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Iframe from 'components/helpers/Iframe';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { logWarning } from 'lib/util/errors';
import fnNoOp from 'lib/util/no-op';

export type IFrameProps = Sitecore.Override.ComponentBase & Components.General.IFrame.Fields.IFrame;
export type BuildIframeUrlProps = Components.General.IFrame.Fields.IFrame & {
  pageUrl?: string;
};

export const buildIframeUrl = ({ fields, pageUrl }: BuildIframeUrlProps): string => {
  let iframeUrl = (fields?.src?.value as string) || '';
  const appendToUrl = (append?: string): string => {
    if ((append || ``).trim().length === 0) {
      return iframeUrl;
    }
    const cbAppend = iframeUrl.includes(`?`) ? `&` : `?`;
    return `${iframeUrl}${cbAppend}${append}`;
  };
  if (fields?.copyUTMParameters?.value === true) {
    const queryParts = pageUrl?.split('?') || [];
    if (queryParts.length > 1) {
      iframeUrl = appendToUrl(queryParts[1].trim());
    }
  }
  if (fields?.includeParentUrlInQuery?.value === true) {
    iframeUrl = appendToUrl(
      `nmdp-frame-parent=${encodeURIComponent(process.env.PUBLIC_URL || '')}`
    );
  }
  return iframeUrl;
};

const IFrame = ({ fields }: IFrameProps): JSX.Element => {
  const router = useRouter();
  const [, setHeight] = useState(0);
  const isEE = useExperienceEditor();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return fnNoOp;
    }

    let addEventMethod: string;
    let removeEventMethod: string;
    if (typeof window.addEventListener === 'function') {
      addEventMethod = 'addEventListener';
      removeEventMethod = 'removeEventListener';
    } else {
      addEventMethod = 'attachEvent';
      removeEventMethod = 'detatchEvent';
    }
    const eventer = window[addEventMethod as keyof Window];
    const deEventer = window[removeEventMethod as keyof Window];

    const messageEvent = addEventMethod === 'attachEvent' ? 'onmessage' : 'message';

    const messageEventHandler = (e: MessageEvent) => {
      // If the message is a resize frame request
      const dataString = (e.data as string).toString();
      if (dataString.indexOf('resize::') !== -1) {
        const iframeHeight = e.data.replace('resize::', '');
        const newHeight = parseInt(iframeHeight, 10) + 30;
        setHeight(newHeight);
        const iframe = document.getElementsByTagName('iframe')[0];
        if (iframe) {
          iframe.style.height = `${newHeight}px`;
        }
      }
    };
    eventer(messageEvent, messageEventHandler, false);
    return () => {
      deEventer(messageEvent, messageEventHandler);
    };
  }, [setHeight]);

  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }

  const srcWithQuerystring = buildIframeUrl({ fields, pageUrl: router?.asPath });

  let styleClass = { '': null };
  if (fields?.style?.value?.trim()) {
    try {
      styleClass = JSON.parse(fields?.style?.value);
    } catch (e) {
      logWarning(`An error occurred setting style an iframe styleClass property`, { fields });
    }
  }

  return (
    <div data-component="authorable/general/iframe">
      {isEE && <p>Click here to edit IFrame Component in Experience Editor</p>}
      <Iframe
        src={isEE ? fields?.src?.value : srcWithQuerystring}
        height={fields?.height?.value}
        width="100%"
        style={styleClass}
        title={fields?.accessibilityTitle?.value}
      />
    </div>
  );
};
export default IFrame;
